/**
 * Minimal typed client for the Quickback API.
 *
 * Auth: browsers use the shared .kardoe.com session cookie; the native iOS
 * shell injects its Keychain-backed OAuth bearer token.
 *
 * API_BASE is same-origin by default. The Quickback Worker serves this SPA
 * on os.kardoe.com (and the meet / leftover CRM hosts) and handles /api,
 * /auth, /broadcast, /realtime and /storage on that same origin — relative
 * URLs, no CORS. The iOS shell is the exception: it serves bundled assets
 * from capacitor://localhost, so relative URLs would miss the API entirely
 * and it needs the absolute origin.
 */
import { authenticatedFetch, isNativeIOS, NATIVE_AUTH_REQUIRED_EVENT } from "./native-auth";

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)
    ?? (isNativeIOS() ? "https://os.kardoe.com" : "");

// Central Kardoe Account SPA. Its .kardoe.com session cookie is shared with
// the CRM API and app hosts, so the user returns already authenticated.
export const AUTH_ORIGIN = "https://internal.kardoe.com";
const LOGIN_URL = import.meta.env.DEV
    ? "/account/login"
    : `${AUTH_ORIGIN}/account/login`;
let redirecting = false;

/**
 * Send the browser to the login page, preserving where the user was headed.
 * Guarded so a burst of parallel 401s only triggers one navigation.
 */
export function redirectToLogin(): void {
    if (redirecting || typeof window === "undefined") return;
    redirecting = true;
    if (isNativeIOS()) {
        window.dispatchEvent(new Event(NATIVE_AUTH_REQUIRED_EVENT));
        // Let the shell show its sign-in screen. A later successful sign-in
        // reloads the app and resets this module-scoped burst guard.
        return;
    }
    const returnTo = encodeURIComponent(window.location.href);
    window.location.href = `${LOGIN_URL}?redirectTo=${returnTo}`;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
    }
    const res = await authenticatedFetch(`${API_BASE}${path}`, {
        ...init,
        headers,
    });
    if (!res.ok) {
        // 401 = no/expired session (the API uses 403 for forbidden). Bounce to
        // login rather than surfacing a raw error on every panel.
        if (res.status === 401) redirectToLogin();
        let body: unknown;
        try { body = await res.json(); } catch { body = await res.text(); }
        const err = new Error(`HTTP ${res.status} ${path}`) as Error & { status: number; body: unknown };
        err.status = res.status;
        err.body = body;
        throw err;
    }
    if (res.status === 204) return undefined as T;
    return await res.json() as T;
}

/**
 * Like call(), but for the UNAUTHENTICATED public booking endpoints. Sends NO
 * credentials and never bounces to login. The public /book pages are served
 * from branded domains (meetings.*) that are cross-site to the API, so a
 * logged-in visitor's .co.kardoe.com cookie would otherwise ride along and the
 * CSRF guard would reject the public POST as a "cross-site cookie-backed
 * mutation". These endpoints authenticate via slug/token, not the cookie, so
 * omitting credentials is both correct and avoids third-party-cookie issues.
 */
async function publicCall<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
    const res = await fetch(`${API_BASE}${path}`, { ...init, credentials: "omit", headers });
    if (!res.ok) {
        let body: unknown;
        try { body = await res.json(); } catch { body = await res.text(); }
        const err = new Error(`HTTP ${res.status} ${path}`) as Error & { status: number; body: unknown };
        err.status = res.status;
        err.body = body;
        throw err;
    }
    if (res.status === 204) return undefined as T;
    return await res.json() as T;
}

export interface Paged<T> {
    data: T[];
    pagination?: {
        total?: number;
        nextCursor?: string | null;
        prevCursor?: string | null;
    };
}
export type ProductKey = "live-room" | "attend";

/**
 * Drop keys whose value is `null`/`undefined` before sending a create/update
 * body. The generated CRUD validation treats a Quickback `.optional()` column
 * as "absent", not "nullable" — passing `null` for an optional field 400s with
 * "expected string, received null". Omitting the key entirely satisfies it.
 * Only null/undefined are stripped; `false`, `0`, and `""` are kept.
 */
function omitNullish<T extends object>(o: T): Partial<T> {
    const out: Partial<T> = {};
    for (const k in o) {
        const v = o[k];
        if (v !== null && v !== undefined) out[k] = v;
    }
    return out;
}

/**
 * React Query options for the big directory lists (contacts, companies, leads).
 * These are expensive to fetch (paged past the 100-row cap) and change rarely,
 * so we keep them fresh for 15 min and retained for an hour — and rely on
 * surgical `setQueryData` cache writes on edit rather than refetching.
 */
export const directoryQueryOptions = {
    staleTime: 15 * 60_000,
    gcTime: 60 * 60_000,
} as const;

/**
 * Page through a list endpoint until every row is fetched. The API clamps
 * `limit` to 100, so a single request can't return large directories
 * (contacts/companies run to thousands after the Pipedrive import). Loops on
 * `offset` until a short page comes back.
 */
async function listAll<T>(path: string, params: Record<string, string> = {}): Promise<T[]> {
    const pageSize = 100;
    const out: T[] = [];
    for (let offset = 0; ; offset += pageSize) {
        const qs = new URLSearchParams({ ...params, limit: String(pageSize), offset: String(offset) }).toString();
        const res = await call<Paged<T>>(`${path}?${qs}`);
        out.push(...res.data);
        if (res.data.length < pageSize || offset > 50_000) break;
    }
    return out;
}

export interface ProductProfile {
    key: string;
    name: string;
    fromAddress: string | null;
    fromName: string;
    bccAddress: string | null;
    /** Campaign sender; null = campaigns use `fromAddress`. */
    bulkFromAddress: string | null;
    configured: boolean;
}

export interface EmailTemplate {
    id: string;
    productKey: ProductKey | null;
    name: string;
    subject: string;
    contentJson: unknown;
    contentHtml: string;
    contentText?: string | null;
    variables?: Array<{ key: string; default?: string }>;
}

export interface Contact {
    id: string;
    productKey: ProductKey | null;
    eventId: string | null;          // nullable — org-scoped, event link is optional
    companyId: string | null;        // nullable — business account link (Pipedrive org)
    company_label?: string | null;   // resolved display label for companyId
    name: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;            // nullable — directory contacts may have only a name/phone
    phone: string | null;
    jobTitle: string | null;
    role: string | null;
    isPrimary: boolean;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    country?: string | null;
    createdAt?: string;
    modifiedAt?: string;
}

export interface Company {
    id: string;
    pipedriveId: string | null;
    name: string;
    peopleCount: number | null;
    labels: string | null;
    city: string | null;
    region: string | null;
    country: string | null;
    address: string | null;
    employees: string | null;
    vertical: string | null;
    ownerName: string | null;
    notes: string | null;
    createdAt?: string;
    modifiedAt?: string;
}

export type LeadStage = "new" | "contacted" | "qualified" | "proposal" | "won" | "lost";

export interface Lead {
    id: string;
    productKey: ProductKey | null;
    companyId: string | null;
    company_label?: string | null;
    contactId: string | null;
    contact_label?: string | null;
    title: string;
    stage: LeadStage;
    value: number | null;
    currency: string | null;
    source: string | null;
    ownerName: string | null;
    expectedCloseDate: string | null;
    stageEnteredAt: string | null;
    position: number | null;
    notes: string | null;
    createdAt?: string;
    modifiedAt?: string;
}

export interface EmailSubscription {
    id: string;
    productKey: ProductKey;
    email: string;
    status: "subscribed" | "unsubscribed";
    reason: "user" | "manual" | "bounce" | "complaint" | null;
    changedAt: string;
    createdAt?: string;
    modifiedAt?: string;
}

export interface Event {
    id: string;
    productKey: ProductKey | null;
    name: string;
    startAt: string | null;
    endAt?: string | null;
    city: string | null;
    region?: string | null;
    country: string | null;
    venue: string | null;
    status?: "new" | "qualified" | "contacted" | "won" | "archived";
    notes?: string | null;
}

export interface Meeting {
    id: string;
    productKey: ProductKey | null;
    organizerUserId: string;
    organizerEmail: string;
    title: string;
    description: string | null;
    location: string | null;
    startAt: string;
    endAt: string;
    timeZone: string;
    attendeeEmails: string[];
    meetUrl: string | null;
    status: "pending" | "scheduled" | "cancelled" | "failed";
    /** Host-recorded result: held, missed, or not yet recorded. */
    outcome?: "completed" | "no_show" | null;
    outcomeAt?: string | null;
    lastError: string | null;
    source?: "crm" | "inbound_invite" | "calendar";
    granolaNoteId?: string | null;
    /** Tiptap JSON doc string — the meeting notepad (Notepad DO mirror). */
    notes?: string | null;
    createdAt?: string;
    modifiedAt?: string;
}

export interface MeetingActivity {
    meetingId: string;
    title: string;
    startAt: string;
    endAt: string;
    timeZone: string;
    attendeeEmail: string;
    responseStatus: "needsAction" | "accepted" | "tentative" | "declined" | "cancelled";
    meetUrl: string | null;
    /** Always false now that no calendar provider is connected — the stored
     *  RSVP is whatever we last recorded, never live. */
    synced: boolean;
}

export interface MeetingInvite {
    id: string;
    meetingId: string;
    email: string;
    name: string | null;
    role: "organizer" | "attendee";
    rsvpStatus: "needs_action" | "accepted" | "declined" | "tentative";
    rsvpAt: string | null;
    rsvpComment: string | null;
    status: "pending" | "sent" | "failed";
    lastSentAt: string | null;
    lastError: string | null;
}

export interface Message {
    id: string;
    productKey: ProductKey | null;
    direction: "inbound" | "outbound";
    status: string;
    messageIdHeader: string | null;
    inReplyTo: string | null;
    threadKey: string;
    fromAddress: string;
    fromName: string | null;
    toAddress: string;
    toName: string | null;
    ccAddress?: string | null;
    bccAddress?: string | null;
    subject: string | null;
    bodyText: string | null;
    bodyHtml: string | null;
    /** cid → R2 key map so the web viewer can resolve inline images. */
    inlineAssets?: { contentId: string; key: string }[] | null;
    draftAttachments?: EmailAttachment[] | null;
    /** Calendar invite (text/calendar part) an inbound email carried. */
    calendarInvite?: CalendarInvite | null;
    receivedAt: string | null;
    sentAt: string | null;
    createdAt: string;
    modifiedAt?: string;
}

export interface CalendarInvite {
    method: string;
    uid: string;
    sequence: number | null;
    summary: string | null;
    description: string | null;
    location: string | null;
    startAt: string | null;
    endAt: string | null;
    timeZone: string;
    allDay: boolean;
    organizer: { email: string; name: string | null } | null;
    attendees: Array<{ email: string; name: string | null; partstat: string | null }>;
    recipient: string | null;
}

export type RsvpResponse = "accepted" | "tentative" | "declined";
export type RsvpStatus = RsvpResponse | "needs_action";

export interface MessageInviteState {
    ok: true;
    invite: CalendarInvite;
    /** Which of our addresses was invited (the one an RSVP answers as). */
    myEmail: string | null;
    myResponse: RsvpStatus;
    /** The live meeting — may have been rescheduled/cancelled by a later email. */
    meeting: {
        id: string;
        status: string;
        title: string;
        startAt: string | null;
        endAt: string | null;
        timeZone: string;
        location: string | null;
        sequence: number | null;
    } | null;
    attendees: Array<{ email: string; name: string | null; role: string; rsvpStatus: RsvpStatus; rsvpComment: string | null }>;
    superseded: boolean;
}

export const messageInvites = {
    get: (messageId: string) =>
        call<MessageInviteState | { ok: false; error: string }>(
            "/api/v1/messages/invite",
            { method: "POST", body: JSON.stringify({ messageId }) },
        ),
    respond: (body: { messageId: string; response: RsvpResponse; comment?: string; notifyOrganizer?: boolean }) =>
        call<(MessageInviteState & { replySent: boolean }) | { ok: false; error: string; detail?: string }>(
            "/api/v1/messages/invite/respond",
            { method: "POST", body: JSON.stringify(body) },
        ),
};

export interface MailboxThreadState {
    threadKey: string;
    lastSeenAt: string | null;
    archivedAt: string | null;
    trashedAt: string | null;
    starred: boolean;
    labels: string[];
}

export type MailboxView = "inbox" | "sent" | "starred" | "unread" | "all" | "archive" | "trash" | "label";
export type MailLabelColor = "gray" | "red" | "orange" | "yellow" | "green" | "blue" | "purple";

export interface MailLabel {
    id: string;
    name: string;
    color: MailLabelColor;
    position: number;
    threadCount?: number;
}

export interface MailboxThread {
    threadKey: string;
    activityAt: string;
    latestInboundAt: string | null;
    latestOutboundAt: string | null;
    messageCount: number;
    unread: boolean;
    archived: boolean;
    trashed: boolean;
    starred: boolean;
    labels: MailLabel[];
    latestMessage: Message;
}

export interface MailboxThreadsResponse {
    ok: true;
    view: MailboxView;
    threads: MailboxThread[];
    pagination: { nextCursor: string | null };
}

export interface MailboxSummary {
    ok: true;
    inboxUnread: number;
}

export type GmailView = "inbox" | "sent" | "starred" | "archive" | "trash" | "all";

export interface GmailConnectionStatus {
    id: string;
    providerEmail: string;
    status: "active" | "error";
    connectedAt: string;
    lastSyncedAt: string | null;
    lastError: string | null;
    backfillCompletedAt: string | null;
    threadCount: number;
}

export interface GmailThreadSummary {
    gmailThreadId: string;
    subject: string | null;
    snippet: string | null;
    counterparty: string | null;
    direction: "inbound" | "outbound";
    latestAt: string;
    messageCount: number;
    unread: boolean;
    starred: boolean;
    inInbox: boolean;
    inSent: boolean;
    archived: boolean;
    trashed: boolean;
    shared: boolean;
}

export interface GmailAttachment {
    attachmentId: string;
    filename: string;
    mimeType: string;
    size: number;
}

export interface GmailMessage {
    id: string;
    gmailMessageId: string;
    direction: "inbound" | "outbound";
    fromAddress: string;
    fromName: string | null;
    toAddress: string;
    ccAddress: string | null;
    subject: string | null;
    bodyHtml: string | null;
    bodyText: string | null;
    snippet: string | null;
    internalDate: string;
    attachments: GmailAttachment[];
    share: { sharedMessageId: string; productKey: ProductKey | null; sharedAt: string } | null;
}

export interface GmailThreadDetail {
    gmailThreadId: string;
    subject: string | null;
    latestAt: string;
    shared: boolean;
    messages: GmailMessage[];
}

export type NotificationCategory = "mail" | "task" | "contact" | "calendar" | "meeting" | "chat" | "system";

export interface AppNotification {
    id: string;
    category: NotificationCategory;
    title: string;
    body: string | null;
    url: string | null;
    sourceType: string | null;
    sourceId: string | null;
    occurredAt: string;
    readAt: string | null;
}

export interface NotificationPreferences {
    inAppEnabled: boolean;
    webPushEnabled: boolean;
    nativeEnabled: boolean;
    mailEnabled: boolean;
    taskEnabled: boolean;
    contactEnabled: boolean;
    calendarEnabled: boolean;
    meetingEnabled: boolean;
    chatEnabled: boolean;
    quietStart: string | null;
    quietEnd: string | null;
    timeZone: string | null;
}

export type AppearanceColorMode = "system" | "light" | "dark";
export type AppearanceAccent = "indigo" | "blue" | "teal" | "green" | "orange" | "rose" | "neutral";

export interface WorkspaceAppearance {
    colorMode: AppearanceColorMode;
    accentTheme: AppearanceAccent;
}

export interface WorkspaceAppearanceResponse {
    ok: true;
    organizationId: string;
    appearance: WorkspaceAppearance;
}

export interface EmailAttachment {
    key: string;
    filename: string;
    contentType: string;
    size: number;
    disposition: "attachment" | "inline";
    contentId?: string;
}

export interface EmailAttachmentUpload {
    url: string;
    method: "PUT";
    headers: Record<string, string>;
    key: string;
    expiresAt: string;
}

// Templates — route is kebab-case per Quickback's URL convention.
export const templates = {
    list: () => call<Paged<EmailTemplate>>("/api/v1/email-templates?limit=100&sort=-createdAt"),
    get:  (id: string) => call<EmailTemplate>(`/api/v1/email-templates/${id}`),
    create: (body: Omit<EmailTemplate, "id">) =>
        call<EmailTemplate>("/api/v1/email-templates", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: Partial<Omit<EmailTemplate, "id">>) =>
        call<EmailTemplate>(`/api/v1/email-templates/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    delete: (id: string) =>
        call<void>(`/api/v1/email-templates/${id}`, { method: "DELETE" }),
};

export const contacts = {
    get: (id: string) => call<Contact>(`/api/v1/contacts/${encodeURIComponent(id)}`),
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "100", sort: "name", ...params }).toString();
        return call<Paged<Contact>>(`/api/v1/contacts?${qs}`);
    },
    /** Fetch every contact (pages past the API's 100-row cap). */
    listAll: (params: Record<string, string> = {}) =>
        listAll<Contact>("/api/v1/contacts", { sort: "name", ...params }),
    /**
     * Search contact fields and the linked company's name across the complete
     * server-side directory. Company matches are resolved to ids first, then
     * fetched through the generated, firewalled contacts endpoint.
     */
    searchAll: async (search: string): Promise<Contact[]> => {
        const term = search.trim();
        if (!term) return listAll<Contact>("/api/v1/contacts", { sort: "name" });

        const [directMatches, matchingCompanies] = await Promise.all([
            listAll<Contact>("/api/v1/contacts", { sort: "name", search: term }),
            listAll<Company>("/api/v1/companies", { sort: "name", "name.like": term }),
        ]);

        // Keep each `companyId.in` URL comfortably below common proxy limits.
        const companyIds = matchingCompanies.map((company) => company.id);
        const companyIdChunks: string[][] = [];
        for (let i = 0; i < companyIds.length; i += 50) {
            companyIdChunks.push(companyIds.slice(i, i + 50));
        }
        const companyMatches = (await Promise.all(companyIdChunks.map((ids) =>
            listAll<Contact>("/api/v1/contacts", {
                sort: "name",
                "companyId.in": ids.join(","),
            }),
        ))).flat();

        const unique = new Map<string, Contact>();
        for (const contact of [...directMatches, ...companyMatches]) unique.set(contact.id, contact);
        return [...unique.values()].sort((a, b) =>
            (a.name ?? a.email ?? "").localeCompare(b.name ?? b.email ?? "", undefined, { sensitivity: "base" }),
        );
    },
    create: (body: Pick<Contact, "productKey" | "eventId" | "name" | "email" | "role" | "isPrimary"> & Partial<Pick<Contact, "companyId" | "phone" | "jobTitle">>) =>
        call<Contact>("/api/v1/contacts", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, patch: Partial<Pick<Contact, "productKey" | "eventId" | "companyId" | "name" | "email" | "phone" | "jobTitle" | "role" | "isPrimary">>) =>
        call<Contact>(`/api/v1/contacts/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    save: (body: {
        id?: string;
        productKey: ProductKey | null;
        eventId: string | null;
        companyId: string | null;
        firstName: string | null;
        lastName: string | null;
        email: string;
        phone: string | null;
        jobTitle: string | null;
        role: string | null;
        isPrimary: boolean;
        addressLine1?: string | null;
        addressLine2?: string | null;
        city?: string | null;
        state?: string | null;
        postalCode?: string | null;
        country?: string | null;
    }) =>
        call<{ ok: boolean; contact?: Contact; error?: string }>(
            "/api/v1/contacts/save",
            { method: "POST", body: JSON.stringify(body) },
        ),
    delete: (id: string) =>
        call<void>(`/api/v1/contacts/${id}`, { method: "DELETE" }),
};

/**
 * Junction row making contact↔product many-to-many. A contact with no rows is
 * unaffiliated. The legacy single `contacts.productKey` still exists alongside
 * this (kept for back-compat).
 */
export interface ContactProduct {
    id: string;
    contactId: string;
    productKey: ProductKey;
}

export const contactProducts = {
    list: (contactId: string) => {
        const qs = new URLSearchParams({ contactId, limit: "100" }).toString();
        return call<Paged<ContactProduct>>(`/api/v1/contact-products?${qs}`);
    },
    create: (body: { contactId: string; productKey: ProductKey }) =>
        call<ContactProduct>("/api/v1/contact-products", { method: "POST", body: JSON.stringify(omitNullish(body)) }),
    delete: (id: string) =>
        call<void>(`/api/v1/contact-products/${id}`, { method: "DELETE" }),
};

export const emailSubscriptions = {
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "500", sort: "-changedAt", ...params }).toString();
        return call<Paged<EmailSubscription>>(`/api/v1/email-subscriptions?${qs}`);
    },
    set: (body: {
        productKey: ProductKey;
        email: string;
        status: "subscribed" | "unsubscribed";
        reason?: "manual";
    }) =>
        call<{ ok: boolean; subscription?: EmailSubscription; error?: string }>(
            "/api/v1/email/subscription",
            { method: "POST", body: JSON.stringify(body) },
        ),
};

export const events = {
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "100", sort: "-startAt", ...params }).toString();
        return call<Paged<Event>>(`/api/v1/events?${qs}`);
    },
    importCsv: (
        csv: string,
        defaultStatus: "new" | "qualified" = "new",
        productKey: ProductKey | null = null,
    ) =>
        call<{ ok: boolean; inserted?: number; updated?: number; skipped?: number; totalRows?: number; failures?: unknown[]; error?: string }>(
            "/api/v1/events/import",
            { method: "POST", body: JSON.stringify({ csv, defaultStatus, productKey }) },
        ),
    create: (body: {
        productKey: ProductKey | null;
        name: string;
        startAt?: string;
        endAt?: string;
        city?: string;
        region?: string;
        country?: string;
        venue?: string;
        status?: Event["status"];
        notes?: string;
    }) => call<Event>("/api/v1/events", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: Partial<{
        productKey: ProductKey | null;
        name: string;
        startAt: string | null;
        endAt: string | null;
        city: string | null;
        region: string | null;
        country: string | null;
        venue: string | null;
        status: Event["status"];
        notes: string | null;
    }>) => call<Event>(`/api/v1/events/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    delete: (id: string) =>
        call<void>(`/api/v1/events/${id}`, { method: "DELETE" }),
};

export const companies = {
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "500", sort: "name", ...params }).toString();
        return call<Paged<Company>>(`/api/v1/companies?${qs}`);
    },
    /** Fetch every company (pages past the API's 100-row cap). */
    listAll: (params: Record<string, string> = {}) =>
        listAll<Company>("/api/v1/companies", { sort: "name", ...params }),
    get: (id: string) => call<Company>(`/api/v1/companies/${id}`),
    create: (body: Partial<Omit<Company, "id">> & { name: string }) =>
        call<Company>("/api/v1/companies", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: Partial<Omit<Company, "id">>) =>
        call<Company>(`/api/v1/companies/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    delete: (id: string) =>
        call<void>(`/api/v1/companies/${id}`, { method: "DELETE" }),
};

export const leads = {
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "100", sort: "-stageEnteredAt", ...params }).toString();
        return call<Paged<Lead>>(`/api/v1/leads?${qs}`);
    },
    /** Fetch every lead (pages past the API's 100-row cap). */
    listAll: (params: Record<string, string> = {}) =>
        listAll<Lead>("/api/v1/leads", { sort: "-stageEnteredAt", ...params }),
    // The generated CRUD schema validates these as `z.string().optional()` —
    // it rejects `null`. Omit empty fields rather than passing null.
    create: (body: {
        title: string;
        stage?: LeadStage;
        productKey?: ProductKey;
        companyId?: string;
        contactId?: string;
        value?: number;
        currency?: string;
        source?: string;
        ownerName?: string;
        notes?: string;
    }) => call<Lead>("/api/v1/leads", { method: "POST", body: JSON.stringify(body) }),
    update: (id: string, body: Partial<Omit<Lead, "id" | "company_label" | "contact_label">>) =>
        call<Lead>(`/api/v1/leads/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
    delete: (id: string) =>
        call<void>(`/api/v1/leads/${id}`, { method: "DELETE" }),
    setStage: (id: string, stage: LeadStage, position?: number) =>
        call<{ ok: boolean; lead?: Lead; error?: string }>(
            "/api/v1/leads/set-stage",
            { method: "POST", body: JSON.stringify({ id, stage, position }) },
        ),
};

export interface FeedBusyCalendar {
    id: string;
    label: string;
    color: string | null;
    source: "ics";
    intervals: { start: string; end: string; allDay?: boolean; title?: string }[];
    error: string | null;
}

/** Busy intervals per pulled-in calendar feed, for the /calendar view. */
export const calendarFeed = {
    busy: (from: string, to: string) =>
        call<{ ok: boolean; calendars: FeedBusyCalendar[] }>(
            "/api/v1/calendar/feed-busy",
            { method: "POST", body: JSON.stringify({ from, to, tz: Intl.DateTimeFormat().resolvedOptions().timeZone }) },
        ),
};

export const meetings = {
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "200", sort: "-startAt", ...params }).toString();
        return call<Paged<Meeting>>(`/api/v1/meetings?${qs}`);
    },
    get: (id: string) => call<Meeting>(`/api/v1/meetings/${encodeURIComponent(id)}`),
    setOutcome: (meetingId: string, outcome: "completed" | "no_show" | null) =>
        call<{ ok: boolean; error?: string }>("/api/v1/meetings/set-outcome", { method: "POST", body: JSON.stringify({ meetingId, outcome }) }),
    /** Soft-delete without notifying guests (calendar/inbound rows only). */
    remove: (meetingId: string) =>
        call<OkResult>("/api/v1/meetings/delete", { method: "POST", body: JSON.stringify({ meetingId }) }),
    activity: (attendeeEmails: string[]) =>
        call<{ ok: boolean; activity: MeetingActivity | null }>(
            "/api/v1/meetings/activity",
            { method: "POST", body: JSON.stringify({ attendeeEmails }) },
        ),
    schedule: (body: {
        productKey: ProductKey | null;
        title: string;
        description?: string | null;
        location?: string | null;
        startAt: string;
        endAt: string;
        timeZone: string;
        attendeeEmails: string[];
        /** Mint a built-in video room; wins over a pasted meetUrl. */
        createVideoRoom?: boolean;
        /** Bring-your-own conference link. */
        meetUrl?: string | null;
    }) =>
        call<{
            ok: boolean;
            meeting?: Meeting;
            /** Always "ics" — the CRM emails the invite itself. */
            transport?: "ics";
            invitesSent?: number;
            invitesFailed?: { email: string; error: string }[];
            /** Final join link (minted room, or the pasted one). */
            meetUrl?: string | null;
            error?: string;
            details?: string;
        }>(
            "/api/v1/meetings/schedule",
            { method: "POST", body: JSON.stringify(body) },
        ),
    update: (body: {
        meetingId: string;
        title?: string;
        description?: string | null;
        location?: string | null;
        meetUrl?: string | null;
        createVideoRoom?: boolean;
        startAt?: string;
        endAt?: string;
        timeZone?: string;
        attendeeEmails?: string[];
        /** Re-send the .ics to attendees (default true server-side). */
        notify?: boolean;
    }) =>
        call<{
            ok: boolean;
            meeting?: Meeting;
            invitesFailed?: { email: string; error: string }[];
            error?: string;
            details?: string;
        }>(
            "/api/v1/meetings/update",
            { method: "POST", body: JSON.stringify(body) },
        ),
    cancel: (meetingId: string) =>
        call<{ ok: boolean; meeting?: Meeting; error?: string; details?: string }>(
            "/api/v1/meetings/cancel",
            { method: "POST", body: JSON.stringify({ meetingId }) },
        ),
};

export interface MeetingArtifact {
    id: string;
    meetingId: string;
    sessionId: string;
    kind: "chat" | "transcript" | "summary";
    /** chat/transcript: CSV as exported by RealtimeKit; summary: plain text. */
    content: string;
    createdAt: string;
}

export const meetingArtifacts = {
    list: (meetingId: string) => {
        const qs = new URLSearchParams({ limit: "50", meetingId, sort: "createdAt" }).toString();
        return call<Paged<MeetingArtifact>>(`/api/v1/meeting-artifacts?${qs}`);
    },
};

export const meetingInvites = {
    list: (meetingId: string) => {
        const qs = new URLSearchParams({ limit: "100", meetingId, sort: "createdAt" }).toString();
        return call<Paged<MeetingInvite>>(`/api/v1/meeting-invites?${qs}`);
    },
};

/** TipTap document — `{ type: "doc", content: [...] }`. Treated opaquely on
 *  the server (stored as JSON); the EmailEditor on the client both produces
 *  it via `getJSON()` and consumes it via the `content` prop. */
export type TipTapDoc = { type: "doc"; content?: unknown[] };

/** A named entry in a user's signature library. */
export interface SignatureRecord {
    id: string;
    name: string;
    content: TipTapDoc;
    isDefault: boolean;
}

export interface UserSettings {
    /** Content of the default signature — drives auto-insert in composers. */
    signature: TipTapDoc | null;
    /** Full named-signature library for this product. */
    signatures: SignatureRecord[];
    productKey: ProductKey;
}

/** Per-user settings (the signature library). Org-scoped on the server via the
 *  session — the same user can have different signatures per org/product. */
export const userSettings = {
    get: (productKey: ProductKey) =>
        call<{ ok: boolean; settings?: UserSettings; error?: string }>(
            `/api/v1/me/settings?productKey=${encodeURIComponent(productKey)}`,
            { method: "GET" },
        ),
    /** Pass `signatures` to replace the library (default mirrored into
     *  `signature`), or the legacy `signature` (`null` clears) to set just the
     *  single default. */
    update: (body: {
        productKey: ProductKey;
        signature?: TipTapDoc | null;
        signatures?: SignatureRecord[];
    }) =>
        call<{ ok: boolean; settings?: UserSettings; error?: string }>(
            "/api/v1/me/settings",
            { method: "POST", body: JSON.stringify(body) },
        ),
};

export const products = {
    list: () =>
        call<{ ok: boolean; products: ProductProfile[]; error?: string }>(
            "/api/v1/email/products",
            { method: "GET" },
        ),
};

export const messages = {
    get: (id: string) => call<Message>(`/api/v1/messages/${encodeURIComponent(id)}`),
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "100", sort: "-createdAt", ...params }).toString();
        return call<Paged<Message>>(`/api/v1/messages?${qs}`);
    },
    send: (templateId: string, contactId: string, productKey?: ProductKey, overrides?: Record<string, string>) =>
        call<{ ok: boolean; messageId?: string; error?: string }>(
            "/api/v1/messages/send",
            { method: "POST", body: JSON.stringify({ templateId, contactId, productKey, overrides }) },
        ),
    sendFullWidth: (body: {
        to?: string | string[];
        cc?: string | string[];
        bcc?: string | string[];
        contactIds?: string[];
        contactId?: string;
        productKey?: ProductKey;
        subject: string;
        bodyHtml?: string;
        bodyText?: string;
        bccOutbound?: boolean;
        inReplyTo?: string;
        campaign?: boolean;
        deliveryMode?: "group" | "individual";
        attachments?: EmailAttachment[];
    }) =>
        call<{
            ok: boolean;
            messageId?: string;
            recipients?: number;
            deliveryMode?: "group" | "individual";
            message?: unknown;
            messages?: unknown[];
            sent?: number;
            failedRecipient?: string;
            error?: string;
            details?: string;
        }>(
            "/api/v1/messages/sendFullWidth",
            { method: "POST", body: JSON.stringify(body) },
        ),
    prepareAttachmentUpload: (body: {
        filename: string;
        contentType: string;
        size: number;
        disposition: "attachment" | "inline";
    }) =>
        call<{
            ok: boolean;
            upload?: EmailAttachmentUpload;
            attachment?: EmailAttachment;
            error?: string;
        }>(
            "/api/v1/email-attachments/prepare",
            { method: "POST", body: JSON.stringify(body) },
        ),
    saveDraft: (body: {
        draftId?:  string;
        attachments?: EmailAttachment[];
        productKey?: ProductKey;
        subject?:  string;
        bodyHtml?: string;
        bodyText?: string;
        toAddress?: string;
        ccAddress?: string;
        bccAddress?: string;
        inReplyTo?: string;
    }) =>
        call<{ ok: boolean; draft?: Message; error?: string }>(
            "/api/v1/messages/saveDraft",
            { method: "POST", body: JSON.stringify(body) },
        ),
    deleteDraft: (draftId: string) =>
        call<{ ok: boolean; error?: string }>(
            "/api/v1/messages/deleteDraft",
            { method: "POST", body: JSON.stringify({ draftId }) },
        ),
};

export const mailboxThreadStates = {
    list: () => call<{ ok: true; states: MailboxThreadState[] }>(
        "/api/v1/mailbox/thread-states",
        { method: "GET" },
    ),
    update: (states: Array<{
        threadKey: string;
        lastSeenAt?: string;
        archived?: boolean;
        trashed?: boolean;
        starred?: boolean;
        labels?: string[];
    }>) => call<{ ok: true; updated: number }>(
        "/api/v1/mailbox/thread-states",
        { method: "POST", headers: { "Idempotency-Key": crypto.randomUUID() }, body: JSON.stringify({ states }) },
    ),
};

export const mailbox = {
    threads: (params: {
        view: MailboxView;
        labelId?: string;
        search?: string;
        recipientRole?: "to-me" | "cc-me";
        productKey?: ProductKey;
        limit?: number;
        cursor?: string;
    }) => {
        const qs = new URLSearchParams({ view: params.view, limit: String(params.limit ?? 50) });
        if (params.labelId) qs.set("labelId", params.labelId);
        if (params.search) qs.set("search", params.search);
        if (params.recipientRole) qs.set("recipientRole", params.recipientRole);
        if (params.productKey) qs.set("productKey", params.productKey);
        if (params.cursor) qs.set("cursor", params.cursor);
        return call<MailboxThreadsResponse>(`/api/v1/mailbox/threads?${qs}`);
    },
    updateThreads: (operation: "archive" | "unarchive" | "trash" | "restore" | "read" | "unread" | "star" | "unstar", threadKeys: string[]) =>
        call<{ ok: true; operation: string; threadKeys: string[]; inboxUnread: number }>("/api/v1/mailbox/threads/state", {
            method: "POST",
            headers: { "Idempotency-Key": crypto.randomUUID() },
            body: JSON.stringify({ operation, threadKeys }),
        }),
    summary: () => call<MailboxSummary>("/api/v1/mailbox/summary"),
    rebuildThreads: () => call<{ ok: true; threads: number; messages: number }>("/api/v1/mailbox/threads/rebuild", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: "{}",
    }),
    labels: () => call<{ ok: true; labels: MailLabel[] }>("/api/v1/mailbox/labels"),
    mutateLabel: (body:
        | { operation: "create"; name: string; color?: MailLabelColor }
        | { operation: "update"; id: string; name?: string; color?: MailLabelColor; position?: number }
        | { operation: "delete"; id: string }) =>
        call<{ ok: true; label?: MailLabel; id?: string }>("/api/v1/mailbox/labels/mutate", {
            method: "POST",
            headers: { "Idempotency-Key": crypto.randomUUID() },
            body: JSON.stringify(body),
        }),
    updateThreadLabels: (operation: "add" | "remove" | "replace", threadKeys: string[], labelIds: string[]) =>
        call<{ ok: true }>("/api/v1/mailbox/threads/labels", {
            method: "POST",
            headers: { "Idempotency-Key": crypto.randomUUID() },
            body: JSON.stringify({ operation, threadKeys, labelIds }),
        }),
    importPreferences: (archivedThreadKeys: string[], readAtByThread: Array<{ threadKey: string; lastReadAt: string }>) =>
        call<{ ok: true; imported: number }>("/api/v1/mailbox/preferences/import", {
            method: "POST",
            headers: { "Idempotency-Key": crypto.randomUUID() },
            body: JSON.stringify({ archivedThreadKeys, readAtByThread }),
        }),
};

export const gmail = {
    connection: () => call<{
        ok: true;
        configured: boolean;
        connection: GmailConnectionStatus | null;
    }>("/api/v1/gmail/connection"),
    startConnection: () => call<{ ok: true; authorizationUrl: string }>("/api/v1/gmail/connect/start", {
        method: "POST",
        body: "{}",
    }),
    completeConnection: (code: string, state: string) => call<{
        ok: true;
        providerEmail: string;
        sync: { synced: number; backfillComplete: boolean; lastSyncedAt: string | null };
        warning?: string;
    }>("/api/v1/gmail/connect/complete", {
        method: "POST",
        body: JSON.stringify({ code, state }),
    }),
    disconnect: () => call<{ ok: true }>("/api/v1/gmail/disconnect", {
        method: "POST",
        body: JSON.stringify({ confirm: true }),
    }),
    sync: () => call<{ ok: true; synced: number; backfillComplete: boolean; lastSyncedAt: string }>("/api/v1/gmail/sync", {
        method: "POST",
        body: "{}",
    }),
    threads: (params: { view: GmailView; search?: string; cursor?: string; limit?: number }) => {
        const query = new URLSearchParams({ view: params.view, limit: String(params.limit ?? 25) });
        if (params.search) query.set("search", params.search);
        if (params.cursor) query.set("starting_after", params.cursor);
        return call<{
            ok: true;
            connected: boolean;
            threads: GmailThreadSummary[];
            pagination: { nextCursor: string | null };
        }>(`/api/v1/gmail/threads?${query}`);
    },
    thread: (gmailThreadId: string) => call<{
        ok: true;
        providerEmail: string;
        thread: GmailThreadDetail;
    }>(`/api/v1/gmail/thread?gmailThreadId=${encodeURIComponent(gmailThreadId)}`),
    shareThread: (body: { gmailThreadId: string; gmailMessageIds?: string[]; productKey?: ProductKey | null }) => call<{
        ok: true;
        gmailThreadId: string;
        sharedThreadKey: string;
        sharedMessageIds: string[];
        created: number;
        alreadyPresent: number;
    }>("/api/v1/gmail/thread/share", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify(body),
    }),
};

export const notifications = {
    list: () => call<{ ok: true; notifications: AppNotification[]; unread: number }>(
        "/api/v1/notifications/inbox",
    ),
    updateState: (body: { ids?: string[]; all?: boolean; dismissed?: boolean }) =>
        call<{ ok: true; updated: number }>("/api/v1/notifications/state", {
            method: "POST",
            body: JSON.stringify(body),
        }),
    preferences: () => call<{ ok: true; preferences: NotificationPreferences }>(
        "/api/v1/notifications/preferences",
    ),
    updatePreferences: (body: Partial<NotificationPreferences>) =>
        call<{ ok: true; preferences: NotificationPreferences }>("/api/v1/notifications/preferences", {
            method: "POST",
            body: JSON.stringify(body),
        }),
};

export type AgentActionStatus = "pending" | "claimed" | "applied" | "failed" | "rejected";

/** A write an agent attempted, held for approval (see agent-gate.ts). */
export interface AgentAction {
    id: string;
    source: "mcp" | "api";
    clientId: string | null;
    toolName: string | null;
    title: string;
    method: "POST" | "PUT" | "PATCH" | "DELETE";
    path: string;
    body: string | null;
    status: AgentActionStatus;
    resolvedAt: string | null;
    resultStatus: number | null;
    resultText: string | null;
    createdAt: string;
}

export const agentActions = {
    list: () => call<Paged<AgentAction>>("/api/v1/agent-actions?sort=-createdAt&limit=100"),
    decide: (id: string, decision: "approve" | "reject") =>
        call<{ ok: true; action: AgentAction; replay: { method: string; path: string; body: string | null } | null } | { ok: false; error: string }>(
            "/api/v1/agent-actions/decide",
            { method: "POST", body: JSON.stringify({ id, decision }) },
        ),
    complete: (id: string, resultStatus: number, resultText?: string) =>
        call<{ ok: boolean }>("/api/v1/agent-actions/complete", {
            method: "POST",
            body: JSON.stringify({ id, resultStatus, resultText }),
        }),
    /** Claim, replay under the approver's own credentials, record the outcome. */
    async approve(id: string): Promise<{ status: number; text: string }> {
        const claimed = await agentActions.decide(id, "approve");
        if (!claimed.ok || !claimed.replay) throw new Error(claimed.ok ? "Nothing to replay" : claimed.error);
        const { method, path, body } = claimed.replay;
        let status = 0;
        let text = "";
        try {
            const res = await authenticatedFetch(`${API_BASE}${path}`, {
                method,
                headers: body ? { "Content-Type": "application/json" } : undefined,
                body: body ?? undefined,
            });
            status = res.status;
            text = (await res.text()).slice(0, 4000);
        } catch (err) {
            text = err instanceof Error ? err.message : String(err);
        }
        await agentActions.complete(id, status, text);
        return { status, text };
    },
};

export const workspaceAppearance = {
    get: () => call<WorkspaceAppearanceResponse>("/api/v1/me/appearance"),
    update: (body: Partial<WorkspaceAppearance>) =>
        call<WorkspaceAppearanceResponse>("/api/v1/me/appearance", {
            method: "POST",
            headers: { "Idempotency-Key": crypto.randomUUID() },
            body: JSON.stringify(body),
        }),
};

export const pushSubscriptions = {
    register: (body: { endpoint: string; p256dh: string; auth: string; userAgent: string }) =>
        call<{ ok: true; id: string }>("/api/v1/notifications/browser", {
            method: "POST",
            headers: { "Idempotency-Key": crypto.randomUUID() },
            body: JSON.stringify(body),
        }),
};

// ─── Gifts & Shipping (Shippo) ──────────────────────────────────────

export interface Gift {
    id: string;
    name: string;
    description: string | null;
    weightOz: string;
    lengthIn: string;
    widthIn: string;
    heightIn: string;
    valueUsd: string | null;
    active: boolean;
    createdAt?: string;
    modifiedAt?: string;
}

export interface ShipFromAddress {
    fromName: string;
    fromCompany: string | null;
    fromStreet1: string;
    fromStreet2: string | null;
    fromCity: string;
    fromState: string;
    fromZip: string;
    fromCountry: string;
    fromPhone: string | null;
    fromEmail: string | null;
    labelFileType: string;
}

export interface ShippoRate {
    object_id: string;
    amount: string;
    currency: string;
    provider: string;
    provider_image_75?: string;
    servicelevel: { name: string; token: string };
    estimated_days?: number;
    duration_terms?: string;
}

export interface Shipment {
    id: string;
    contactId: string | null;
    giftId: string | null;
    status: "draft" | "rates_ready" | "purchased" | "failed";
    shippoShipmentId: string | null;
    shippoRateId: string | null;
    carrier: string | null;
    service: string | null;
    amount: string | null;
    currency: string | null;
    trackingNumber: string | null;
    trackingUrl: string | null;
    labelUrl: string | null;
    rates: ShippoRate[] | null;
    addressTo: Record<string, unknown> | null;
    addressFrom: Record<string, unknown> | null;
    lastError: string | null;
    createdAt?: string;
    modifiedAt?: string;
}

export interface AddressOverride {
    name: string;
    company?: string;
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    phone?: string;
    email?: string;
}

export interface ParcelOverride {
    weightOz?: number;
    lengthIn?: number;
    widthIn?: number;
    heightIn?: number;
}

export const gifts = {
    list: (includeInactive = false) =>
        call<{ ok: boolean; gifts: Gift[]; error?: string }>(
            `/api/v1/gifts/list?includeInactive=${includeInactive}`,
        ),
    save: (body: {
        id?: string;
        name: string;
        description?: string | null;
        weightOz: number;
        lengthIn: number;
        widthIn: number;
        heightIn: number;
        valueUsd?: number | null;
        active?: boolean;
    }) =>
        call<{ ok: boolean; gift?: Gift; error?: string }>(
            "/api/v1/gifts/save",
            { method: "POST", body: JSON.stringify(body) },
        ),
    delete: (id: string) =>
        call<{ ok: boolean; error?: string }>(
            "/api/v1/gifts/delete",
            { method: "POST", body: JSON.stringify({ id }) },
        ),
};

export const shipping = {
    settings: () =>
        call<{ ok: boolean; configured: boolean; shippoConfigured: boolean; settings: ShipFromAddress; error?: string }>(
            "/api/v1/shipping/settings",
        ),
    saveSettings: (body: ShipFromAddress) =>
        call<{ ok: boolean; settings?: ShipFromAddress; error?: string }>(
            "/api/v1/shipping/settings/save",
            { method: "POST", body: JSON.stringify(body) },
        ),
    list: (limit = 100) =>
        call<{ ok: boolean; shipments: Shipment[]; error?: string }>(
            `/api/v1/shipping/list?limit=${limit}`,
        ),
    create: (body: { contactId: string; giftId: string; addressFrom?: AddressOverride; parcel?: ParcelOverride }) =>
        call<{ ok: boolean; shipment?: Shipment; rates?: ShippoRate[]; error?: string; details?: string }>(
            "/api/v1/shipping/create",
            { method: "POST", body: JSON.stringify(body) },
        ),
    buy: (shipmentId: string, rateId: string) =>
        call<{ ok: boolean; shipment?: Shipment; error?: string; details?: string }>(
            "/api/v1/shipping/buy",
            { method: "POST", body: JSON.stringify({ shipmentId, rateId }) },
        ),
};

export interface MessageRecipient {
    id: string;
    messageId: string;
    address: string;
    contactId: string | null;
    providerMessageId: string | null;
    status: "sent" | "delivered" | "bounced" | "complained" | "failed";
    subType: string | null;
    eventAt: string | null;
}

export const messageRecipients = {
    /** Per-recipient delivery rows for a group send (empty for single sends). */
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "200", ...params }).toString();
        return call<Paged<MessageRecipient>>(`/api/v1/message-recipients?${qs}`);
    },
};

export const auth = {
    /** Better Auth session, or null/`{}` when logged out. */
    session: () => call<{ user?: { id?: string } | null } | null>("/auth/v1/get-session"),
    /** Active organization incl. members — the only place the caller's org role is exposed. */
    organization: () => call<{ members?: Array<{ userId: string; role: string }> } | null>("/auth/v1/organization/get-full-organization"),
};

export const mobileDevices = {
    register: (body: {
        installationId: string;
        platform: "ios";
        pushToken: string;
        environment: "development" | "production";
    }) => call<{ ok: boolean; error?: string }>(
        "/api/v1/mobile/devices/register",
        {
            method: "POST",
            headers: { "Idempotency-Key": crypto.randomUUID() },
            body: JSON.stringify(body),
        },
    ),
};

// ─── Booking / Scheduling (Calendly-style) ──────────────────────────

export type AppointmentLocationType = "video" | "phone" | "in_person" | "custom";

/** One recurring working-hours window for a weekday (host wall-clock). */
export interface WeeklyAvailabilityWindow {
    dayOfWeek: number;   // 0 = Sunday … 6 = Saturday
    startTime: string;   // "09:00"
    endTime:   string;   // "17:00"
}

/** A date-specific blackout or extra-hours override. */
export interface DateOverride {
    date:       string;            // "2026-12-25" (host-local)
    available:  boolean;           // false = full-day blackout
    startTime?: string;
    endTime?:   string;
}

export interface AppointmentType {
    id: string;
    ownerUserId: string;
    /** Branded product this type lives under. null = default domain. */
    productKey: ProductKey | null;
    slug: string;
    name: string;
    description: string | null;
    /** Template for the booked calendar event title; null → falls back to name. */
    calendarEventTitle: string | null;
    durationMinutes: number;
    locationType: AppointmentLocationType;
    locationDetail: string | null;
    slotIntervalMinutes: number | null;
    bufferBeforeMinutes: number;
    bufferAfterMinutes: number;
    minNoticeMinutes: number;
    maxAdvanceDays: number;
    dailyLimit: number | null;
    timeZone: string;
    weeklyAvailability: WeeklyAvailabilityWindow[];
    dateOverrides: DateOverride[];
    color: string | null;
    active: boolean;
    createdAt?: string;
    modifiedAt?: string;
}

/** Fields the create/update CRUD accepts (guards.createable / updatable). */
export interface AppointmentTypeInput {
    ownerUserId?: string;
    productKey?: ProductKey | null;
    slug: string;
    name: string;
    description?: string | null;
    calendarEventTitle?: string | null;
    durationMinutes: number;
    locationType?: AppointmentLocationType;
    locationDetail?: string | null;
    slotIntervalMinutes?: number | null;
    bufferBeforeMinutes?: number;
    bufferAfterMinutes?: number;
    minNoticeMinutes?: number;
    maxAdvanceDays?: number;
    dailyLimit?: number | null;
    timeZone: string;
    weeklyAvailability?: WeeklyAvailabilityWindow[];
    dateOverrides?: DateOverride[];
    color?: string | null;
    active?: boolean;
}

/** Generated CRUD, like contacts/gifts (kebab-case route). */
export const appointmentTypes = {
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "100", sort: "name", ...params }).toString();
        return call<Paged<AppointmentType>>(`/api/v1/appointment-types?${qs}`);
    },
    get: (id: string) => call<AppointmentType>(`/api/v1/appointment-types/${id}`),
    create: (body: AppointmentTypeInput) =>
        call<AppointmentType>("/api/v1/appointment-types", { method: "POST", body: JSON.stringify(omitNullish(body)) }),
    update: (id: string, body: Partial<AppointmentTypeInput>) =>
        call<AppointmentType>(`/api/v1/appointment-types/${id}`, { method: "PATCH", body: JSON.stringify(omitNullish(body)) }),
    delete: (id: string) =>
        call<void>(`/api/v1/appointment-types/${id}`, { method: "DELETE" }),
};

export type BookingCalendarSource = "ics";

export interface BookingCalendar {
    id: string;
    userId: string;
    source: BookingCalendarSource;
    label: string;
    color: string | null;
    icsUrl: string | null;
    checkConflicts: boolean;
    enabled: boolean;
    lastSyncedAt: string | null;
    lastError: string | null;
    createdAt?: string;
    modifiedAt?: string;
}

/** Generated CRUD. `icsUrl`/`cachedBody` are server-only (never returned). */
export const bookingCalendars = {
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "200", sort: "label", ...params }).toString();
        return call<Paged<BookingCalendar>>(`/api/v1/booking-calendars?${qs}`);
    },
    create: (body: {
        userId?: string;
        source: BookingCalendarSource;
        label: string;
        color?: string | null;
        icsUrl?: string | null;
        checkConflicts?: boolean;
        enabled?: boolean;
    }) =>
        call<BookingCalendar>("/api/v1/booking-calendars", { method: "POST", body: JSON.stringify(omitNullish(body)) }),
    update: (id: string, body: Partial<Pick<BookingCalendar, "label" | "color" | "checkConflicts" | "enabled">>) =>
        call<BookingCalendar>(`/api/v1/booking-calendars/${id}`, { method: "PATCH", body: JSON.stringify(omitNullish(body)) }),
    delete: (id: string) =>
        call<void>(`/api/v1/booking-calendars/${id}`, { method: "DELETE" }),
};

/**
 * Maps a product to the branded host its public booking links use. The
 * `default` row (productKey null) is the fallback host; per-product rows
 * (live-room → meet.tryliveroom.com, attend → meet.attendvip.com)
 * override it for types tagged with that product.
 */
/** "default" is the fallback host used by types with no product. The column is
 *  NOT NULL, so the default row is the literal string "default" (not null). */
export type DomainProductKey = ProductKey | "default";

export interface BookingDomain {
    id: string;
    productKey: DomainProductKey;
    domain: string;
    active: boolean;
}

/** Generated CRUD, like booking-calendars (member+, kebab-case route). */
export const bookingDomains = {
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "100", sort: "domain", ...params }).toString();
        return call<Paged<BookingDomain>>(`/api/v1/booking-domains?${qs}`);
    },
    create: (body: { productKey: DomainProductKey; domain: string; active?: boolean }) =>
        call<BookingDomain>("/api/v1/booking-domains", { method: "POST", body: JSON.stringify(omitNullish(body)) }),
    update: (id: string, body: Partial<Pick<BookingDomain, "domain" | "active">>) =>
        call<BookingDomain>(`/api/v1/booking-domains/${id}`, { method: "PATCH", body: JSON.stringify(omitNullish(body)) }),
    delete: (id: string) =>
        call<void>(`/api/v1/booking-domains/${id}`, { method: "DELETE" }),
};

export interface AvailabilitySlot {
    startIso: string;
    endIso: string;
}

/** Custom authed scheduling actions (member+). */
export const scheduling = {
    /** Built-in video (RealtimeKit) configured on the backend? Gates the "Video call" location option. */
    videoStatus: () =>
        call<{ ok: boolean; configured: boolean }>("/api/v1/scheduling/video-status"),
    availabilityPreview: (body: { appointmentTypeId: string; from: string; to: string; tz: string }) =>
        call<{ ok: boolean; slots: AvailabilitySlot[]; error?: string }>(
            "/api/v1/scheduling/availability-preview",
            { method: "POST", body: JSON.stringify(body) },
        ),
    suggestedTimes: (body: { appointmentTypeId: string; slots: string[]; messageId?: string }) =>
        call<{ ok: boolean; token?: string; bookingUrlBase?: string; error?: string }>(
            "/api/v1/scheduling/suggested-times",
            { method: "POST", body: JSON.stringify(body) },
        ),
};

export interface Booking {
    id: string;
    appointmentTypeId: string | null;
    contactId: string | null;
    meetingId: string | null;
    ownerUserId: string;
    inviteeName: string | null;
    inviteeEmail: string;
    inviteeTimeZone: string | null;
    inviteeNotes: string | null;
    startAt: string;
    endAt: string;
    status: "confirmed" | "cancelled" | "rescheduled" | "no_show";
    source: "public_page" | "email_suggested" | "manual";
    cancelReason: string | null;
    rescheduledFromId: string | null;
    createdAt?: string;
    modifiedAt?: string;
}

/** Authed booking management (host-side). Reads via generated CRUD `read`. */
export const bookingsApi = {
    list: (params: Record<string, string> = {}) => {
        const qs = new URLSearchParams({ limit: "200", sort: "-startAt", ...params }).toString();
        return call<Paged<Booking>>(`/api/v1/bookings?${qs}`);
    },
    cancel: (id: string, reason?: string) =>
        call<{ ok: boolean; error?: string }>(
            "/api/v1/bookings/cancel",
            { method: "POST", body: JSON.stringify({ id, reason }) },
        ),
    setStatus: (id: string, status: "no_show" | "confirmed") =>
        call<{ ok: boolean; error?: string }>(
            "/api/v1/bookings/set-status",
            { method: "POST", body: JSON.stringify({ id, status }) },
        ),
};

// ─── Public booking (UNauthenticated GET/POST to /api/v1/public/…) ──
//
// These power the public /book/* pages. They never send the session cookie
// requirement — the backend actions are access {roles:["PUBLIC"]}. `call`
// still attaches credentials (harmless) and surfaces non-2xx as throws, but
// the page-level error:"…" payloads are returned in-band for slot_taken etc.

export interface PublicBookingType {
    slug: string;
    name: string;
    description: string | null;
    durationMinutes: number;
    locationType: AppointmentLocationType;
    timeZone: string;
    color: string | null;
    hostName: string | null;
    /** Product the link belongs to (drives branding). */
    productKey: ProductKey | null;
    /** Display brand for the product (e.g. "Attend", "Live Room", "Kardoe"). */
    brand: string;
    /** Marketing site for the brand (e.g. https://attend.vip), or null. */
    brandUrl: string | null;
}

export interface PublicBookingResult {
    id: string;
    startAt: string;
    endAt: string;
    manageToken: string;
    meetUrl: string | null;
}

export interface SuggestedSlot {
    startIso: string;
    endIso: string;
    available: boolean;
}

export interface SuggestedSet {
    token: string;
    appointmentName: string;
    durationMinutes: number;
    timeZone: string;
    hostName: string | null;
    slots: SuggestedSlot[];
    status: "active" | "booked" | "expired";
    productKey: ProductKey | null;
    brand: string;
    /** Marketing site for the brand (e.g. https://attend.vip), or null. */
    brandUrl: string | null;
    /** Appointment type slug — "Back to times" links to the full /book/<slug>. */
    slug: string | null;
}

export interface ManageBooking {
    id: string;
    appointmentName: string;
    startAt: string;
    endAt: string;
    status: Booking["status"];
    inviteeName: string | null;
    timeZone: string;
    meetUrl: string | null;
    canReschedule: boolean;
    slug: string | null;
    productKey: ProductKey | null;
    brand: string;
    /** Marketing site for the brand (e.g. https://attend.vip), or null. */
    brandUrl: string | null;
}

export type BookingError = "slot_taken" | "slot_unavailable" | "invalid" | "expired" | "not_found";

export const publicBooking = {
    meta: (slug: string) =>
        publicCall<{ ok: boolean; type?: PublicBookingType; visitorTimeZone: string | null; error?: string }>(
            `/api/v1/public/booking/meta?slug=${encodeURIComponent(slug)}`,
        ),
    availability: (slug: string, from: string, to: string, tz: string) => {
        const qs = new URLSearchParams({ slug, from, to, tz }).toString();
        return publicCall<{ ok: boolean; slots: AvailabilitySlot[]; timeZone: string; error?: string }>(
            `/api/v1/public/booking/availability?${qs}`,
        );
    },
    book: (body: { slug: string; startIso: string; firstName: string; lastName: string; company?: string; email: string; notes?: string; tz: string }) =>
        publicCall<{ ok: boolean; booking?: PublicBookingResult; error?: BookingError }>(
            "/api/v1/public/booking/book",
            { method: "POST", body: JSON.stringify(body) },
        ),
    suggested: (token: string) =>
        publicCall<{ ok: boolean; set?: SuggestedSet; visitorTimeZone: string | null; error?: string }>(
            `/api/v1/public/suggested?token=${encodeURIComponent(token)}`,
        ),
    suggestedBook: (body: { token: string; startIso: string; firstName: string; lastName: string; company?: string; email: string; notes?: string }) =>
        publicCall<{ ok: boolean; booking?: PublicBookingResult; error?: BookingError }>(
            "/api/v1/public/suggested/book",
            { method: "POST", body: JSON.stringify(body) },
        ),
    manage: (token: string) =>
        publicCall<{ ok: boolean; booking?: ManageBooking; visitorTimeZone: string | null; error?: string }>(
            `/api/v1/public/manage?token=${encodeURIComponent(token)}`,
        ),
    manageCancel: (token: string, reason?: string) =>
        publicCall<{ ok: boolean; error?: string }>(
            "/api/v1/public/manage/cancel",
            { method: "POST", body: JSON.stringify({ token, reason }) },
        ),
    manageReschedule: (token: string, startIso: string) =>
        publicCall<{ ok: boolean; booking?: PublicBookingResult; error?: BookingError }>(
            "/api/v1/public/manage/reschedule",
            { method: "POST", body: JSON.stringify({ token, startIso }) },
        ),
    /** Swap a built-in video room code/id for a RealtimeKit auth token.
     *  `startAt` accompanies error "too_early" so the page can name the meeting
     *  time. The join window itself is deliberately never sent to the guest. */
    meetingJoin: (id: string, name?: string) =>
        publicCall<{ ok: boolean; authToken?: string; error?: string; startAt?: string }>(
            "/api/v1/public/meeting/join",
            { method: "POST", body: JSON.stringify({ id, name }) },
        ),
    /** Host join: sends the signed-in session cookie and returns a host-preset
     *  token for the organizer or an org admin/owner. Resolves null on any
     *  failure (signed out, not the host, network) and never bounces to login,
     *  so the meeting page can fall back to the guest join. */
    meetingHostJoin: async (id: string): Promise<string | null> => {
        try {
            const res = await fetch(`${API_BASE}/api/v1/meetings/host-join`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id }),
            });
            if (!res.ok) return null;
            const body = await res.json() as { ok?: boolean; authToken?: string };
            return body.ok && body.authToken ? body.authToken : null;
        } catch {
            return null;
        }
    },
};

export type ApiError = Error & { status?: number; body?: unknown };

// ─── Loop records — personal capture engine (ported from the loop worker) ───

export type RecordType = "note" | "feedback" | "decision" | "action" | "loop" | "reminder" | "ask";
export type RecordStatus = "open" | "closed" | "dropped";

export interface LoopRecord {
    id: string;
    ownerUserId: string;
    type: RecordType;
    content: string;
    details: string | null;
    status: RecordStatus;
    droppedReason: string | null;
    dueDate: string | null;              // YYYY-MM-DD
    assigneeContactId: string | null;
    assigneeCompanyId: string | null;
    parentRecordId: string | null;
    originMeetingId: string | null;
    dayPage: string | null;              // YYYY-MM-DD
    valence: "positive" | "constructive" | "neutral" | null;
    delivered: boolean;
    fireDate: string | null;             // YYYY-MM-DD
    recurrence: "none" | "annual" | "quarterly" | "monthly";
    leadTime: number;
    answer: string | null;
    memorable: boolean;
    redateCount: number;
    createdAt: string;
    modifiedAt: string;
}

export interface TodayMetrics {
    overdue: number;
    mineToClose: number;
    waitingOn: number;
    dueToday: number;
    meetingsToday: number;
}

export interface TodayBundle {
    ok: boolean;
    forDay: { records: LoopRecord[] };
    leftover: { records: LoopRecord[] };
    meetings: TodayMeeting[];
    /** meetingId → objects explicitly linked to that meeting (emails etc). */
    linked: Record<string, LinkedItem[]>;
    metrics: TodayMetrics;
}

// ─── Unified link graph (email ↔ meeting ↔ contact ↔ record ↔ case …) ────

export type LinkNodeType = "contact" | "company" | "lead" | "event" | "meeting" | "record" | "case" | "thread" | "message";
export interface LinkNode { type: LinkNodeType; id: string }
export interface LinkedItem extends LinkNode { label: string; sublabel: string | null; href: string | null }

export const linksApi = {
    /** Explicit links for a node, plus implicit `related` context (meetings). */
    for: (type: LinkNodeType, id: string) =>
        call<OkResult<{ items: LinkedItem[]; related: LinkedItem[] }>>(`/api/v1/links/for?${new URLSearchParams({ type, id })}`),
    set: (from: LinkNode, add: LinkNode[] = [], remove: LinkNode[] = []) =>
        call<OkResult<{ items: LinkedItem[] }>>("/api/v1/links/set", { method: "POST", body: JSON.stringify({ from, add, remove }) }),
};

export interface RecordPinGroup {
    contactId: string;
    contactName: string;
    items: Array<{ record: LoopRecord; pinnedAt: string }>;
}

export type RecordLinkTarget = "company" | "lead" | "event" | "meeting";

export interface RecordLink {
    recordId: string;
    targetType: RecordLinkTarget;
    targetId: string;
}

export interface CreateRecordBody {
    id?: string;
    type: RecordType;
    content: string;
    details?: string | null;
    mentionContactIds?: string[];
    links?: Array<{ targetType: RecordLinkTarget; targetId: string }>;
    dayPage?: string;
    originMeetingId?: string;
    parentRecordId?: string;
    memorable?: boolean;
    assigneeContactId?: string | null;
    assigneeCompanyId?: string | null;
    dueDate?: string;
    valence?: "positive" | "constructive" | "neutral";
    fireDate?: string;
    recurrence?: "none" | "annual" | "quarterly" | "monthly";
    leadTime?: number;
}

type OkResult<T = Record<string, never>> = ({ ok: true } & T) | { ok: false; error: string };

export const loopRecords = {
    create: (body: CreateRecordBody) =>
        call<OkResult<{ id: string }>>("/api/v1/records/create", { method: "POST", body: JSON.stringify(body) }),
    focus: (ids: string[], tz?: string) =>
        call<OkResult>("/api/v1/records/focus", { method: "POST", body: JSON.stringify({ ids, tz }) }),
    later: (id: string, dueDate: string | null) =>
        call<OkResult>("/api/v1/records/later", { method: "POST", body: JSON.stringify({ id, dueDate }) }),
    assign: (id: string, assigneeContactId?: string | null, assigneeCompanyId?: string | null) =>
        call<OkResult>("/api/v1/records/assign", { method: "POST", body: JSON.stringify({ id, assigneeContactId, assigneeCompanyId }) }),
    close: (ids: string[], answer?: string) =>
        call<OkResult>("/api/v1/records/close", { method: "POST", body: JSON.stringify({ ids, answer }) }),
    drop: (ids: string[], droppedReason?: string) =>
        call<OkResult>("/api/v1/records/drop", { method: "POST", body: JSON.stringify({ ids, droppedReason }) }),
    reopen: (ids: string[]) =>
        call<OkResult>("/api/v1/records/reopen", { method: "POST", body: JSON.stringify({ ids }) }),
    snooze: (id: string, days: number) =>
        call<OkResult<{ fireDate: string }>>("/api/v1/records/snooze", { method: "POST", body: JSON.stringify({ id, days }) }),
    acknowledge: (id: string) =>
        call<OkResult<{ nextFireDate?: string }>>("/api/v1/records/acknowledge", { method: "POST", body: JSON.stringify({ id }) }),
    updateContent: (id: string, content: string, details?: string | null) =>
        call<OkResult>("/api/v1/records/update-content", { method: "POST", body: JSON.stringify({ id, content, details }) }),
    memorable: (id: string, memorable?: boolean) =>
        call<OkResult<{ memorable: boolean }>>("/api/v1/records/memorable", { method: "POST", body: JSON.stringify({ id, memorable }) }),
    pin: (recordId: string, contactId: string) =>
        call<OkResult>("/api/v1/records/pin", { method: "POST", body: JSON.stringify({ recordId, contactId }) }),
    unpin: (recordId: string, contactId: string) =>
        call<OkResult>("/api/v1/records/unpin", { method: "POST", body: JSON.stringify({ recordId, contactId }) }),
    byType: (types: RecordType[], opts: { status?: RecordStatus; memorable?: boolean; limit?: number } = {}) => {
        const qs = new URLSearchParams({ types: types.join(",") });
        if (opts.status) qs.set("status", opts.status);
        if (opts.memorable) qs.set("memorable", "true");
        if (opts.limit) qs.set("limit", String(opts.limit));
        return call<OkResult<{ records: LoopRecord[] }>>(`/api/v1/records/by-type?${qs}`);
    },
    leftover: (before: string) =>
        call<OkResult<{ records: LoopRecord[] }>>(`/api/v1/records/leftover?before=${before}`),
    recent: (q?: string, limit?: number) => {
        const qs = new URLSearchParams();
        if (q) qs.set("q", q);
        if (limit) qs.set("limit", String(limit));
        return call<OkResult<{ records: LoopRecord[] }>>(`/api/v1/records/recent?${qs}`);
    },
    todayBundle: (date: string, tz?: string) => {
        const qs = new URLSearchParams({ date });
        if (tz) qs.set("tz", tz);
        return call<TodayBundle>(`/api/v1/records/today-bundle?${qs}`);
    },
    pins: () => call<OkResult<{ groups: RecordPinGroup[] }>>("/api/v1/records/pins"),
    links: (recordIds: string[]) =>
        call<OkResult<{ links: RecordLink[] }>>(`/api/v1/records/links?recordIds=${encodeURIComponent(recordIds.join(","))}`),
    setLinks: (id: string, links: Array<{ targetType: RecordLinkTarget; targetId: string }>) =>
        call<OkResult<{ links: Array<{ targetType: RecordLinkTarget; targetId: string }> }>>(
            "/api/v1/records/set-links",
            { method: "POST", body: JSON.stringify({ id, links }) },
        ),
};

// ─── Loop phase 2/3: meetings on Today, day notes, contact ledger ─────────

export interface TodayMeeting {
    id: string;
    title: string;
    startAt: string;
    endAt: string;
    timeZone: string;
    location: string | null;
    meetUrl: string | null;
    status: "pending" | "scheduled" | "cancelled" | "failed";
    source: "crm" | "inbound_invite";
    attendeeEmails: string[];
    granolaNoteId: string | null;
}

export interface DayNote {
    id: string;
    date: string;
    notes: string | null;   // Tiptap JSON doc string
    modifiedAt: string;
}

export interface QuietContact {
    id: string; name: string | null; email: string | null;
    lastTouched: string; days: number;
    lead: { id: string; title: string; stage: string } | null;
}

export const loopExtras = {
    goingQuiet: (days = 30) =>
        call<OkResult<{ contacts: QuietContact[] }>>(`/api/v1/contacts/going-quiet?days=${days}`),
    forMeeting: (meetingId: string) =>
        call<OkResult<{ records: LoopRecord[] }>>(`/api/v1/records/for-meeting?meetingId=${encodeURIComponent(meetingId)}`),
    forContact: (contactId: string, status?: RecordStatus) => {
        const qs = new URLSearchParams({ contactId });
        if (status) qs.set("status", status);
        return call<OkResult<{ records: LoopRecord[]; mentionedRecordIds: string[] }>>(`/api/v1/records/for-contact?${qs}`);
    },
    getDayNote: (date: string) =>
        call<OkResult<{ note: DayNote | null }>>(`/api/v1/day-notes/get?date=${date}`),
    saveDayNote: (date: string, notes: string | null) =>
        call<OkResult<{ id: string }>>("/api/v1/day-notes/save", { method: "POST", body: JSON.stringify({ date, notes }) }),
};

export interface GranolaCandidate {
    id: string;
    title: string;
    startedAt: string | null;
    timeDeltaMs: number | null;
    titleScore: number;
    overall: number;
}

export interface GranolaTranscript {
    ok: true;
    id: string;
    title: string | null;
    summary: string | null;
    summaryMarkdown: string | null;
    transcript: Array<{ speaker: string | null; text: string }>;
    source: string;
    fetchedAt: number | null;
}

export const meetingFeed = {
    get: () => call<OkResult<{ url: string | null }>>("/api/v1/me/meeting-feed"),
    reset: () => call<OkResult<{ url: string }>>("/api/v1/me/meeting-feed/reset", { method: "POST", body: JSON.stringify({}) }),
};

export const granola = {
    config: () => call<OkResult<{ configured: boolean; lastFour: string | null }>>("/api/v1/granola/config"),
    setKey: (key: string) =>
        call<OkResult<{ configured: boolean; lastFour: string }>>("/api/v1/granola/set-key", { method: "POST", body: JSON.stringify({ key }) }),
    clearKey: () =>
        call<OkResult<{ configured: boolean }>>("/api/v1/granola/clear-key", { method: "POST", body: JSON.stringify({}) }),
    search: (meetingId: string) =>
        call<OkResult<{ configured: boolean; classification: "exact" | "suggested" | "none"; candidates: GranolaCandidate[]; error?: string }>>(
            `/api/v1/granola/search?meetingId=${encodeURIComponent(meetingId)}`),
    link: (meetingId: string, granolaNoteId: string) =>
        call<OkResult>("/api/v1/granola/link", { method: "POST", body: JSON.stringify({ meetingId, granolaNoteId }) }),
    unlink: (meetingId: string) =>
        call<OkResult>("/api/v1/granola/unlink", { method: "POST", body: JSON.stringify({ meetingId }) }),
    transcript: (meetingId: string, refresh = false) =>
        call<GranolaTranscript | { ok: false; error: string; suggestUnlink?: boolean }>(
            `/api/v1/granola/transcript?meetingId=${encodeURIComponent(meetingId)}${refresh ? "&refresh=true" : ""}`),
};

// ─── Outbound email provider (org-level) ───────────────────────────

export type EmailProviderName = "cloudflare" | "ses";

export interface EmailProviderConfig {
    /** "resend" is a legacy stored value (no longer supported); treated as unconfigured. */
    provider: EmailProviderName | "resend" | null;
    cloudflareAvailable: boolean;
    /** Server-computed from the caller's org role — chrome only, the POST re-checks. */
    canEdit: boolean;
    /** `secretsPresent` = AWS_ACCESS_KEY_ID + AWS_SECRET_ACCESS_KEY are set as
     *  wrangler secrets on the API deployment. Region and configuration set are
     *  the per-org, non-secret half. */
    ses: { secretsPresent: boolean; region: string | null; configurationSet: string | null };
}

/** Credentials are wrangler secrets, not part of this call. Omit an optional
 *  field to keep the stored value; send "" to clear it. */
export interface EmailProviderInput {
    provider: EmailProviderName;
    sesRegion?: string;
    sesConfigurationSet?: string;
}

export const emailProvider = {
    config: () => call<OkResult<EmailProviderConfig>>("/api/v1/email-provider/config"),
    set: (body: EmailProviderInput) =>
        call<OkResult<EmailProviderConfig>>("/api/v1/email-provider/config", { method: "POST", body: JSON.stringify(body) }),
    clear: () =>
        call<OkResult<{ provider: null }>>("/api/v1/email-provider/clear", { method: "POST", body: JSON.stringify({}) }),
    test: (body: { productKey?: ProductKey } = {}) =>
        call<OkResult<{ provider: EmailProviderName; messageId: string }>>("/api/v1/email-provider/test", { method: "POST", body: JSON.stringify(body) }),
};

// ─── Sending identities (per product) ──────────────────────────────

export interface SenderIdentity {
    productKey: ProductKey;
    name: string;
    fromAddress: string;
    fromName: string;
    replyTo: string;
    bccAddress: string | null;
    unsubscribeBaseUrl: string;
    trackedDomains: string[];
    recipientDomains: string[];
    /** Resolved campaign sender; null = campaigns use the 1:1 identity. */
    bulk: { fromAddress: string; replyTo: string; unsubscribeBaseUrl: string } | null;
    configured: boolean;
    verifiedAt: string | null;
    /** `fromAddress` still equals the deployment env default — nobody has
     *  edited this identity yet. */
    envDefault: boolean;
    /** The raw stored column values, null / [] where nothing is set. The
     *  fields above are the resolved profile (row overlaid on the env
     *  defaults); these are what the form edits, so an untouched fallback
     *  isn't written back as a real value. */
    stored: {
        replyTo: string | null;
        bccAddress: string | null;
        unsubscribeBaseUrl: string | null;
        trackedDomains: string[];
        recipientDomains: string[];
        bulkFromAddress: string | null;
        bulkReplyTo: string | null;
        bulkUnsubscribeBaseUrl: string | null;
    };
}

/** Omit an optional field to keep the stored value; send "" to clear a string,
 *  omit an array to keep it. */
export interface SenderIdentityInput {
    productKey: ProductKey;
    fromAddress: string;
    fromName: string;
    replyTo?: string;
    bccAddress?: string;
    unsubscribeBaseUrl?: string;
    trackedDomains?: string[];
    recipientDomains?: string[];
    bulkFromAddress?: string;
    bulkReplyTo?: string;
    bulkUnsubscribeBaseUrl?: string;
}

export const senderIdentities = {
    list: () => call<OkResult<{ canEdit: boolean; identities: SenderIdentity[] }>>("/api/v1/sender-identities/list"),
    update: (body: SenderIdentityInput) =>
        call<OkResult<{ identity: SenderIdentity; verificationCleared: boolean }>>(
            "/api/v1/sender-identities/update", { method: "POST", body: JSON.stringify(body) }),
    verify: (productKey: ProductKey) =>
        call<{ ok: true; verified: boolean; message: string } | { ok: false; error: string; missingSecret?: string }>(
            "/api/v1/sender-identities/verify", { method: "POST", body: JSON.stringify({ productKey }) }),
};

// ─── Tier 1: record verbs, replies, virtual calendar events ───────────────

export interface VirtualEvent {
    key: string;
    source: "google" | "ics";
    feedLabel: string;
    feedColor: string | null;
    title: string;
    startAt: string;
    endAt: string;
    allDay: boolean;
    location: string | null;
    meetUrl: string | null;
    htmlLink: string | null;
    attendeeEmails: string[];
    googleEventId: string | null;
    icalUid: string | null;
}

export const loopVerbs = {
    delete: (ids: string[]) =>
        call<OkResult>("/api/v1/records/delete", { method: "POST", body: JSON.stringify({ ids }) }),
    changeType: (id: string, type: RecordType) =>
        call<OkResult<{ type: RecordType }>>("/api/v1/records/change-type", { method: "POST", body: JSON.stringify({ id, type }) }),
    deliver: (id: string) =>
        call<OkResult>("/api/v1/records/deliver", { method: "POST", body: JSON.stringify({ id }) }),
    children: (parentId: string) =>
        call<OkResult<{ records: LoopRecord[] }>>(`/api/v1/records/children?parentId=${encodeURIComponent(parentId)}`),
    /** Remove a record from a meeting or day-note surface; soft-deletes it if orphaned. */
    detach: (id: string, from: { meetingId?: string; dayPage?: string }, dryRun = false) =>
        call<OkResult<{ deleted: boolean }>>("/api/v1/records/detach", { method: "POST", body: JSON.stringify({ id, ...from, dryRun }) }),
};

export const dayCalendar = {
    events: (date: string, tz?: string) => {
        const qs = new URLSearchParams({ date });
        if (tz) qs.set("tz", tz);
        return call<OkResult<{ events: VirtualEvent[]; errors: string[] }>>(`/api/v1/calendar/day-events?${qs}`);
    },
    materialize: (event: VirtualEvent) =>
        call<OkResult<{ meetingId: string; created: boolean }>>("/api/v1/calendar/materialize", {
            method: "POST",
            body: JSON.stringify({
                title: event.title,
                startAt: event.startAt,
                endAt: event.endAt,
                location: event.location,
                meetUrl: event.meetUrl,
                htmlLink: event.htmlLink,
                attendeeEmails: event.attendeeEmails,
                googleEventId: event.googleEventId,
                icalUid: event.icalUid,
            }),
        }),
};

export const granolaAdmin = {
    relinkAll: (sinceDays?: number) =>
        call<OkResult<{ scannedMeetings: number; scannedNotes: number; linked: number }>>(
            "/api/v1/granola/relink-all",
            { method: "POST", body: JSON.stringify({ sinceDays }) },
        ),
};

export const loopBatch = {
    /** Batch record fetch by ids — resolves recordRef embed cards. */
    getByIds: (ids: string[]) =>
        call<OkResult<{ records: LoopRecord[] }>>(`/api/v1/records/get?ids=${encodeURIComponent(ids.join(","))}`),
};

export const loopTargets = {
    /** Records attached to a CRM object (reverse of the # tag). */
    forTarget: (targetType: RecordLinkTarget, targetId: string, status?: RecordStatus) => {
        const qs = new URLSearchParams({ targetType, targetId });
        if (status) qs.set("status", status);
        return call<OkResult<{ records: LoopRecord[] }>>(`/api/v1/records/for-target?${qs}`);
    },
};

// ─── Kardoe Chat + Cases ───────────────────────────────────────────

export interface ChatPerson {
    userId: string;
    name: string;
    email: string;
    image: string | null;
    role: string;
}

export interface ChatConversation {
    id: string;
    kind: "direct" | "group" | "channel";
    visibility: "open" | "private";
    name: string | null;
    topic: string | null;
    displayName: string;
    participants: ChatPerson[];
    lastMessageAt: string | null;
    lastMessagePreview: string | null;
    lastMessageAuthor: string | null;
    unread: boolean;
    createdByUserId: string;
}

export interface ChatMessage {
    id: string;
    conversationId: string;
    authorUserId: string;
    authorNameAtSend: string;
    authorImageAtSend: string | null;
    body: string;
    createdAt: string;
    editedAt: string | null;
    clientId?: string | null;
}

export const chat = {
    roster: () => call<{ ok: true; people: ChatPerson[]; currentUserId: string }>("/api/v1/chat/roster"),
    conversations: () => call<{ ok: true; conversations: ChatConversation[] }>("/api/v1/chat/conversations"),
    createConversation: (body: {
        kind: ChatConversation["kind"];
        visibility?: ChatConversation["visibility"];
        name?: string | null;
        topic?: string | null;
        recipientUserIds: string[];
    }) => call<{ ok: true; conversationId: string; created: boolean }>("/api/v1/chat/conversations/create", {
        method: "POST",
        body: JSON.stringify(body),
    }),
    messages: (conversationId: string) =>
        call<{ ok: true; messages: ChatMessage[] }>(`/api/v1/chat/conversations/${encodeURIComponent(conversationId)}/messages?conversationId=${encodeURIComponent(conversationId)}&limit=200`),
    postMessage: (conversationId: string, body: string, clientId?: string) =>
        call<{ ok: true; message: ChatMessage }>(`/api/v1/chat/conversations/${encodeURIComponent(conversationId)}/messages`, {
            method: "POST",
            body: JSON.stringify({ conversationId, body, clientId }),
        }),
    markRead: (conversationId: string) =>
        call<{ ok: true; readAt: string }>(`/api/v1/chat/conversations/${encodeURIComponent(conversationId)}/read`, {
            method: "POST",
            body: JSON.stringify({ conversationId }),
        }),
    updateChannel: (conversationId: string, body: {
        name: string;
        topic?: string | null;
        visibility: "open" | "private";
        memberUserIds: string[];
    }) => call<{ ok: true }>(`/api/v1/chat/channels/${encodeURIComponent(conversationId)}`, {
        method: "PATCH",
        body: JSON.stringify({ conversationId, ...body }),
    }),
};

export type CaseStatus = "open" | "pending" | "on_hold" | "resolved";
export type CasePriority = "low" | "normal" | "high" | "urgent";

export interface KardoeCase {
    id: string;
    subject: string;
    status: CaseStatus;
    priority: CasePriority;
    requesterContactId: string | null;
    requester: Pick<Contact, "id" | "name" | "email"> | null;
    contacts: Pick<Contact, "id" | "name" | "email">[];
    assigneeUserId: string | null;
    assignee: ChatPerson | null;
    openedByUserId: string;
    latestActivityAt: string;
    latestPreview: string | null;
    resolvedAt: string | null;
    createdAt: string;
    unread: boolean;
}

export interface CaseComment {
    id: string;
    caseId: string;
    authorUserId: string;
    authorNameAtSend: string;
    body: string;
    kind: "message" | "note" | "system";
    createdAt: string;
}

export const casesApi = {
    list: (filters: { status?: CaseStatus; priority?: CasePriority; mine?: boolean; includeResolved?: boolean } = {}) => {
        const qs = new URLSearchParams();
        if (filters.status) qs.set("status", filters.status);
        if (filters.priority) qs.set("priority", filters.priority);
        if (filters.mine) qs.set("mine", "true");
        if (filters.includeResolved) qs.set("includeResolved", "true");
        const suffix = qs.size ? `?${qs}` : "";
        return call<{ ok: true; cases: KardoeCase[] }>(`/api/v1/cases/inbox${suffix}`);
    },
    get: (caseId: string) => call<{ ok: true; case: KardoeCase; comments: CaseComment[] }>(
        `/api/v1/cases/${encodeURIComponent(caseId)}/thread?caseId=${encodeURIComponent(caseId)}`,
    ),
    create: (body: {
        subject: string;
        priority: CasePriority;
        requesterContactId?: string | null;
        contactIds?: string[];
        assigneeUserId?: string | null;
        initialMessage?: string | null;
    }) => call<{ ok: true; caseId: string }>("/api/v1/cases/create", {
        method: "POST",
        body: JSON.stringify(body),
    }),
    comment: (caseId: string, body: string, kind: "message" | "note") =>
        call<{ ok: true; comment: CaseComment; reopened: boolean }>(`/api/v1/cases/${encodeURIComponent(caseId)}/comments`, {
            method: "POST",
            body: JSON.stringify({ caseId, body, kind }),
        }),
    update: (caseId: string, patch: {
        status?: CaseStatus;
        priority?: CasePriority;
        assigneeUserId?: string | null;
        contactIds?: string[];
    }) => call<{ ok: true; changed: boolean }>(`/api/v1/cases/${encodeURIComponent(caseId)}/triage`, {
        method: "PATCH",
        body: JSON.stringify({ caseId, ...patch }),
    }),
    markRead: (caseId: string) => call<{ ok: true; readAt: string }>(
        `/api/v1/cases/${encodeURIComponent(caseId)}/read`,
        { method: "POST", body: JSON.stringify({ caseId }) },
    ),
};


export interface ReceivedAttachment { id: string; filename: string; contentType: string; size: number; }
export const receivedAttachments = {
    list: (messageId: string) => call<{ attachments: ReceivedAttachment[] }>(`/api/v1/messages/attachments?${new URLSearchParams({ messageId })}`),
    download: async (messageId: string, attachmentId: string): Promise<Blob> => {
        const response = await authenticatedFetch(`${API_BASE}/api/v1/messages/attachment?${new URLSearchParams({ messageId, attachmentId })}`);
        if (!response.ok) {
            if (response.status === 401) redirectToLogin();
            throw new Error("Could not download attachment.");
        }
        return response.blob();
    },
};
